import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export type PaymentMethodType = 'processor' | 'manual';

export type ManualDetailType = 'bank' | 'crypto' | 'credit_card' | 'custom';
export type PaymentMethodCategory = 'fiat' | 'crypto';

export interface ManualCurrencyConfigPayload {
    currency_id: string;
    detail_type: ManualDetailType;
    details: Record<string, any>;
    sort_order?: number;
}

export interface PaymentMethod {
    id: string;
    name: string;
    code: string;
    display_name: string;
    description?: string;
    note?: string;
    requires_address: boolean;
    requires_card_details: boolean;
    requires_bank_details?: boolean;
    requires_holder_name?: boolean;
    method_category: PaymentMethodCategory;
    is_active: boolean;
    method_type?: PaymentMethodType;
    supports_processors?: boolean;
    processor_config?: any;
    processing_time_hours: number;
    processing_time_description: string;
    min_amount: number;
    max_amount: number;
    fee_percentage: number;
    fee_fixed: number;
    supported_currencies: any[];
    supported_currency_symbols: string[];
    manual_currency_configs?: ManualCurrencyConfigPayload[];
    metadata?: any;
    created_at: string;
    updated_at: string;
}

export interface PaymentMethodCreateRequest {
    name: string;
    code: string;
    display_name: string;
    description?: string;
    note?: string;
    requires_address: boolean;
    requires_card_details: boolean;
    requires_bank_details?: boolean;
    is_active: boolean;
    method_type: PaymentMethodType;
    supports_processors?: boolean;
    processor_config?: any;
    processing_time_hours: number;
    min_amount: number;
    max_amount: number;
    fee_percentage: number;
    fee_fixed: number;
    manual_currency_configs?: ManualCurrencyConfigPayload[];
    metadata?: any;
}

export interface PaymentMethodUpdateRequest extends Partial<PaymentMethodCreateRequest> {
    id: string;
}

export interface PaymentMethodsListParams {
    page?: number;
    per_page?: number;
    search?: string;
    is_active?: boolean;
    method_category?: PaymentMethodCategory;
    currency?: string; // Filter by currency symbol
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}

export interface PaymentMethodsListResponse {
    data: PaymentMethod[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface PaymentMethodStatistics {
    total: number;
    active: number;
    inactive: number;
    fiat: number;
    crypto: number;
    requires_address: number;
    requires_card_details: number;
}

// Get payment methods list with pagination and filters
export const usePaymentMethodsList = (params: PaymentMethodsListParams = {}) => {
    return useQuery<PaymentMethodsListResponse>({
        queryKey: ['payment-methods', 'list', params],
        queryFn: async () => {
            const searchParams = new URLSearchParams();

            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    searchParams.append(key, value.toString());
                }
            });

            const { data } = await httpClient.get<ApiResponse<PaymentMethodsListResponse>>(
                `${API_ENDPOINTS.CRM.PAYMENT_METHODS.LIST}?${searchParams.toString()}`
            );
            return data.data;
        },
    });
};

// Get single payment method
export const usePaymentMethod = (id: string) => {
    return useQuery<PaymentMethod>({
        queryKey: ['payment-methods', 'detail', id],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<PaymentMethod>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.DETAILS(id)
            );
            return data.data;
        },
        enabled: !!id,
    });
};

// Get available currencies
export const useAvailableCurrencies = () => {
    return useQuery<any[]>({
        queryKey: ['payment-methods', 'currencies'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<any[]>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.AVAILABLE_CURRENCIES
            );
            return data.data;
        },
    });
};

// Get payment methods statistics
export const usePaymentMethodsStatistics = () => {
    return useQuery<PaymentMethodStatistics>({
        queryKey: ['payment-methods', 'statistics'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<PaymentMethodStatistics>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.STATISTICS
            );
            return data.data;
        },
    });
};

// Create payment method
export const useCreatePaymentMethod = () => {
    const queryClient = useQueryClient();

    return useMutation<PaymentMethod, Error, PaymentMethodCreateRequest>({
        mutationFn: async paymentMethodData => {
            const { data } = await httpClient.post<ApiResponse<PaymentMethod>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.CREATE,
                paymentMethodData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
        },
    });
};

// Update payment method
export const useUpdatePaymentMethod = () => {
    const queryClient = useQueryClient();

    return useMutation<PaymentMethod, Error, PaymentMethodUpdateRequest>({
        mutationFn: async ({ id, ...paymentMethodData }) => {
            const { data } = await httpClient.put<ApiResponse<PaymentMethod>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.UPDATE(id),
                paymentMethodData
            );
            return data.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
            queryClient.invalidateQueries({
                queryKey: ['payment-methods', 'detail', variables.id],
            });
        },
    });
};

// Delete payment method
export const useDeletePaymentMethod = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async id => {
            await httpClient.delete(API_ENDPOINTS.CRM.PAYMENT_METHODS.DELETE(id));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
        },
    });
};

// Toggle payment method status
export const useTogglePaymentMethodStatus = () => {
    const queryClient = useQueryClient();

    return useMutation<{ message: string; is_active: boolean }, Error, string>({
        mutationFn: async id => {
            const { data } = await httpClient.post<
                ApiResponse<{ message: string; is_active: boolean }>
            >(API_ENDPOINTS.CRM.PAYMENT_METHODS.TOGGLE_STATUS(id));
            return data.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
            queryClient.invalidateQueries({ queryKey: ['payment-methods', 'detail', id] });
            queryClient.invalidateQueries({ queryKey: ['payment-methods', 'statistics'] });
        },
    });
};

// Payment Processor Config types
export interface PaymentProcessorConfig {
    id: string;
    payment_method_id: string;
    tenant_integration_id: string;
    is_enabled: boolean;
    processor_settings?: any;
    processor_name: string;
    integration?: {
        id: string;
        provider_name: string;
        is_active: boolean;
    };
}

export interface AvailableProcessor {
    id: string;
    provider_name: string;
    is_active: boolean;
    configuration: {
        environment: string;
    };
}

export interface ProcessorConfigUpdateRequest {
    processor_configs: Array<{
        tenant_integration_id: string;
        is_enabled: boolean;
        processor_settings?: any;
    }>;
}

// Get processors for a payment method
export const usePaymentMethodProcessors = (paymentMethodId: string) => {
    return useQuery<PaymentProcessorConfig[]>({
        queryKey: ['payment-methods', 'processors', paymentMethodId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<PaymentProcessorConfig[]>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.PROCESSORS(paymentMethodId)
            );
            return data.data;
        },
        enabled: !!paymentMethodId,
    });
};

// Get available processors
export const useAvailableProcessors = () => {
    return useQuery<AvailableProcessor[]>({
        queryKey: ['payment-methods', 'available-processors'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<AvailableProcessor[]>>(
                API_ENDPOINTS.CRM.PAYMENT_METHODS.AVAILABLE_PROCESSORS
            );
            return data.data;
        },
    });
};

// Update payment method processors
export const useUpdatePaymentMethodProcessors = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string; processors: PaymentProcessorConfig[] },
        Error,
        { paymentMethodId: string; data: ProcessorConfigUpdateRequest }
    >({
        mutationFn: async ({ paymentMethodId, data }) => {
            const { data: response } = await httpClient.put<
                ApiResponse<{ message: string; processors: PaymentProcessorConfig[] }>
            >(API_ENDPOINTS.CRM.PAYMENT_METHODS.UPDATE_PROCESSORS(paymentMethodId), data);
            return response.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['payment-methods', 'processors', variables.paymentMethodId],
            });
            queryClient.invalidateQueries({
                queryKey: ['payment-methods', 'detail', variables.paymentMethodId],
            });
        },
    });
};
